import React, { useState, useEffect, useRef } from 'react';
import { BarChart2, CreditCard, FileText, PieChart, Settings, Plus, Download, Upload, Trash2, Edit2, RefreshCw, Briefcase, CheckSquare, Filter, ArrowUpDown } from 'lucide-react';
import { collection, query, getDocs, getDoc, addDoc, updateDoc, doc, deleteDoc, where, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ownerStamp } from '../recordOwner';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import { currentUserInitial as globalInitial, setCurrentUserGlobal } from '../globalState';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const pad = n => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const slug = (v) => String(v || '').toLowerCase().replace(/[^a-z]/g, '');
// Takes the translate function rather than closing over one: both components in this file need
// them. The parameter is deliberately not named `t` -- see i18n/no-shadowed-t.
const settledWord = (tr, v) => tr(`budget.settledStatuses.${slug(v)}`, {}, v);
const budgetStatusWord = (tr, v) => tr(`budget.budgetStatuses.${slug(v)}`, {}, v);
const methodWord = (tr, v) => tr(`budget.methods.${slug(v)}`, {}, v);

const SortFilterHeader = ({ label, columnKey, data, sortConfig, setSortConfig, filterConfig, setFilterConfig, activePopover, setActivePopover }) => {
  const { t } = useT();
  const uniqueValues = React.useMemo(() => {
    const vals = data.map(item => item[columnKey] || '').filter(Boolean);
    return [...new Set(vals)].sort();
  }, [data, columnKey]);

  const isActive = activePopover === columnKey;
  const isSorted = sortConfig.key === columnKey;
  const currentFilters = filterConfig[columnKey] || [];
  const isFiltered = currentFilters.length > 0;

  const toggleSort = (direction) => {
    setSortConfig({ key: columnKey, direction });
    setActivePopover(null);
  };

  const toggleFilter = (val) => {
    const newFilters = currentFilters.includes(val) 
      ? currentFilters.filter(v => v !== val)
      : [...currentFilters, val];
    setFilterConfig(prev => ({ ...prev, [columnKey]: newFilters }));
  };

  const clearFilter = () => {
    setFilterConfig(prev => ({ ...prev, [columnKey]: [] }));
  };

  return (
    <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', userSelect: 'none' }} onClick={() => setActivePopover(isActive ? null : columnKey)}>
        {label}
        {isSorted ? (
          <span style={{ color: 'var(--color-primary)' }}><ArrowUpDown size={14} /></span>
        ) : isFiltered ? (
          <span style={{ color: 'var(--color-primary)' }}><Filter size={14} /></span>
        ) : (
          <span style={{ opacity: 0.3 }}><Filter size={14} /></span>
        )}
      </div>

      {isActive && (
        <div className="filter-popover-container" style={{ position: 'absolute', top: '100%', left: 0, backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', zIndex: 50, width: '200px', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <button className="btn-outline" style={{ textAlign: 'left', padding: '0.25rem 0.5rem', fontSize: '0.75rem', border: 'none', backgroundColor: sortConfig.key === columnKey && sortConfig.direction === 'asc' ? 'var(--color-bg-app)' : 'transparent' }} onClick={(e) => { e.stopPropagation(); toggleSort('asc'); }}>{t('budget.sortAsc')}</button>
            <button className="btn-outline" style={{ textAlign: 'left', padding: '0.25rem 0.5rem', fontSize: '0.75rem', border: 'none', backgroundColor: sortConfig.key === columnKey && sortConfig.direction === 'desc' ? 'var(--color-bg-app)' : 'transparent' }} onClick={(e) => { e.stopPropagation(); toggleSort('desc'); }}>{t('budget.sortDesc')}</button>
          </div>
          <hr style={{ borderTop: '1px solid var(--color-border)', margin: '0.25rem 0' }} />
          <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {uniqueValues.map(val => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', cursor: 'pointer', padding: '0.1rem 0' }}>
                <input type="checkbox" checked={currentFilters.includes(val)} onChange={() => toggleFilter(val)} onClick={e => e.stopPropagation()} />
                {val}
              </label>
            ))}
            {uniqueValues.length === 0 && <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('budget.noValues')}</div>}
          </div>
          <hr style={{ borderTop: '1px solid var(--color-border)', margin: '0.25rem 0' }} />
          <button onClick={(e) => { e.stopPropagation(); clearFilter(); setActivePopover(null); }} style={{ fontSize: '0.75rem', color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: '0.25rem 0.5rem' }}>
            {t('budget.clearFilter')}
          </button>
        </div>
      )}
    </th>
  );
};

const defaultRoleMatrix = {
  Head: { deptBudget: 'No Access', commBudget: 'No Access', deptExpenditure: 'No Access', commExpenditure: 'No Access', deptStatistics: 'No Access', commStatistics: 'No Access' },
  Deputy: { deptBudget: 'No Access', commBudget: 'No Access', deptExpenditure: 'No Access', commExpenditure: 'No Access', deptStatistics: 'No Access', commStatistics: 'No Access' },
  Member: { deptBudget: 'No Access', commBudget: 'No Access', deptExpenditure: 'No Access', commExpenditure: 'No Access', deptStatistics: 'No Access', commStatistics: 'No Access' },
};

const BudgetControl = () => {
  const { t } = useT();
  const [activeTab, setActiveTab] = useState('statistics');
  const { activeSchoolId, isMobile } = useOutletContext();
  
  // Dropdown Options State
  const [academicYears, setAcademicYears] = useState([]);
  const [globalSchoolYear, setGlobalSchoolYear] = useState('');

  useEffect(() => {
    if (!globalSchoolYear && academicYears.length > 0) {
      setGlobalSchoolYear(academicYears[0]);
    }
  }, [academicYears, globalSchoolYear]);
  const [staffs, setStaffs] = useState([]);
  const [fundCodes, setFundCodes] = useState([]);
  const [deptCodes, setDeptCodes] = useState({ subjects: [], committees: [] });
  
  // Settings and Permissions State
  const [budgetSettings, setBudgetSettings] = useState(null);
  const [userDuties, setUserDuties] = useState({ departments: [], committees: [] });
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  const [settingsForm, setSettingsForm] = useState({
    roleMatrix: defaultRoleMatrix,
    accountants: [],
    budgetAdmins: [],
    systemAdmins: []
  });

  useEffect(() => {
    if (budgetSettings) {
      setSettingsForm({
        roleMatrix: budgetSettings.roleMatrix || defaultRoleMatrix,
        accountants: budgetSettings.accountants || [],
        budgetAdmins: budgetSettings.budgetAdmins || [],
        systemAdmins: budgetSettings.systemAdmins || []
      });
    } else {
      setSettingsForm({
        roleMatrix: defaultRoleMatrix,
        accountants: [],
        budgetAdmins: [],
        systemAdmins: []
      });
    }
  }, [budgetSettings]);

  const [savingSettings, setSavingSettings] = useState(false);
  const [newAccountant, setNewAccountant] = useState('');
  const [newBudgetAdmin, setNewBudgetAdmin] = useState('');

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'budget_control_settings'), settingsForm).catch(async (e) => {
        if (e.code === 'not-found') {
          await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'budget_control_settings'), settingsForm);
        } else throw e;
      });
      setBudgetSettings(settingsForm);
      alert(t('budget.settingsSaved'));
    } catch (e) {
      console.error('Error saving settings', e);
      alert(t('budget.settingsSaveFailed'));
    } finally {
      setSavingSettings(false);
    }
  };



  // Search and Multi-select State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExpenditures, setSelectedExpenditures] = useState([]);
  const [selectedBudgets, setSelectedBudgets] = useState([]);
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  // Batch Update
  const [batchUpdateField, setBatchUpdateField] = useState('');
  const [batchUpdateValue, setBatchUpdateValue] = useState('');
  // Table sorting and filtering
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({});
  const [activePopover, setActivePopover] = useState(null);
  
  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activePopover && !e.target.closest('.filter-popover-container')) {
        setActivePopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activePopover]);

  // Log and Current User State
  const [currentUserInitial, setCurrentUserInitial] = useState(globalInitial);
  const [currentUserSysRole, setCurrentUserSysRole] = useState(null);
  const [logStartDate, setLogStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [logEndDate, setLogEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [logTypeFilter, setLogTypeFilter] = useState('both');
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Expenditure State
  const [expenditures, setExpenditures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingExpId, setEditingExpId] = useState(null);

  // Budget State
  const [budgets, setBudgets] = useState([]);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [showBudgetAddForm, setShowBudgetAddForm] = useState(false);
  const [editingBudgetId, setEditingBudgetId] = useState(null);
  
  // Settlement State
  const [editingSettlementId, setEditingSettlementId] = useState(null);
  const [settlementFormData, setSettlementFormData] = useState({});

  const [statsDeptCode, setStatsDeptCode] = useState('');
  const [statsFundCode, setStatsFundCode] = useState('');

  const hasAccessToRecord = React.useCallback((recordDeptCode, contextType, action) => {
    // contextType: 'Budget', 'Expenditure', 'Statistics'
    // action: 'View', 'Edit'
    
    if (budgetSettings) {
      if (budgetSettings.accountants?.includes(currentUserInitial)) return true;
      if (budgetSettings.budgetAdmins?.includes(currentUserInitial)) return true;
      if (budgetSettings.systemAdmins?.includes(currentUserInitial)) return true;
    }

    if (!budgetSettings || !budgetSettings.roleMatrix) return false;

    const isDept = deptCodes.subjects.some(d => d.code === recordDeptCode);
    const isComm = deptCodes.committees.some(c => c.code === recordDeptCode);

    let userRoleForRecord = null;
    let areaPrefix = '';

    if (isDept) {
      const match = userDuties.departments.find(d => d.department === recordDeptCode && d.sch_yr === globalSchoolYear);
      if (match) userRoleForRecord = match.role;
      areaPrefix = 'dept';
    } else if (isComm) {
      const match = userDuties.committees.find(c => c.committee === recordDeptCode && c.sch_yr === globalSchoolYear);
      if (match) userRoleForRecord = match.role;
      areaPrefix = 'comm';
    }

    if (!userRoleForRecord) return false;

    const roleSettings = budgetSettings.roleMatrix[userRoleForRecord];
    if (!roleSettings) return false;

    const areaKey = areaPrefix + contextType;
    const settingValue = roleSettings[areaKey];
    
    if (settingValue === 'Edit') return true;
    if (settingValue === 'View only' && action === 'View') return true;
    if (settingValue === 'View Only' && action === 'View') return true;
    
    return false;
  }, [budgetSettings, currentUserInitial, deptCodes, userDuties, globalSchoolYear]);

  const hasEditAccessToAny = React.useCallback((contextType) => {
    return deptCodes.subjects.some(d => hasAccessToRecord(d.code, contextType, 'Edit')) ||
           deptCodes.committees.some(c => hasAccessToRecord(c.code, contextType, 'Edit'));
  }, [hasAccessToRecord, deptCodes]);

  const statisticsData = React.useMemo(() => {
    if (!globalSchoolYear) return [];
    
    // Structure: { fundCode: { deptCode: { budget: 0, expenditure: 0 } } }
    const grouped = {};
    
    budgets.forEach(b => {
      if (b.status === 'Approved') {
        const fCode = b.fundCode || 'Unspecified';
        const dCode = b.deptCode || 'Unspecified';
        if (!grouped[fCode]) grouped[fCode] = {};
        if (!grouped[fCode][dCode]) grouped[fCode][dCode] = { budget: 0, expenditure: 0 };
        
        let amt = parseFloat(b.amt);
        if (!isNaN(amt)) grouped[fCode][dCode].budget += amt;
      }
    });

    expenditures.forEach(e => {
      const fCode = e.fundCode || 'Unspecified';
        const dCode = e.deptCode || 'Unspecified';
        if (!grouped[fCode]) grouped[fCode] = {};
        if (!grouped[fCode][dCode]) grouped[fCode][dCode] = { budget: 0, expenditure: 0 };
        
        let amt = parseFloat(e.amt);
        if (!isNaN(amt)) grouped[fCode][dCode].expenditure += amt;
    });

    const rows = [];
    Object.keys(grouped).forEach(fCode => {
      Object.keys(grouped[fCode]).forEach(dCode => {
        if (!hasAccessToRecord(dCode, 'Statistics', 'View')) return;
        if (statsDeptCode && dCode !== statsDeptCode) return;
        if (statsFundCode && fCode !== statsFundCode) return;
        
        const row = grouped[fCode][dCode];
        rows.push({
          fundCode: fCode,
          deptCode: dCode,
          budget: row.budget,
          expenditure: row.expenditure,
          remaining: row.budget - row.expenditure
        });
      });
    });

    rows.sort((a, b) => {
      const deptCmp = a.deptCode.localeCompare(b.deptCode);
      if (deptCmp !== 0) return deptCmp;
      return a.fundCode.localeCompare(b.fundCode);
    });

    return rows;
    // hasAccessToRecord must be a dependency: it denies everything until budgetSettings/deptCodes/
    // userDuties have loaded, so a run that happens before then produces an empty table. Without it
    // listed, that empty result stays cached until one of the other dependencies happens to change
    // -- which is why the statistics only appeared after visiting the Budget/Expenditure tab (those
    // re-fetch and hand back a new array identity, forcing a recompute).
  }, [budgets, expenditures, globalSchoolYear, statsDeptCode, statsFundCode, hasAccessToRecord]);
  
  const initialFormState = {
    expId: '',
    schYr: '',
    invDate: '',
    deptCode: '',
    item: '',
    tic: '',
    fundCode: '',
    amt: '',
    settlementMethod: '',
    remarks: '',
    invoiceDoc: '',
    settled: '',
    transNo: '',
    receiptDoc: ''
  };
  const [formData, setFormData] = useState(initialFormState);

  const initialBudgetFormState = {
    budgetId: '',
    schYr: '',
    deptCode: '',
    item: '',
    fundCode: '',
    amt: '',
    status: 'Applied'
  };
  const [budgetFormData, setBudgetFormData] = useState(initialBudgetFormState);

  const isGlobalAdmin = (currentUserSysRole === 'admin') || (budgetSettings ? (
    (budgetSettings.accountants || []).includes(currentUserInitial) ||
    (budgetSettings.budgetAdmins || []).includes(currentUserInitial) ||
    (budgetSettings.systemAdmins || []).includes(currentUserInitial)
  ) : false);

  const tabs = [
    { id: 'statistics', label: t('budget.tabs.statistics'), icon: BarChart2 },
    { id: 'budget', label: t('budget.tabs.budget'), icon: Briefcase },
    { id: 'expenditure', label: t('budget.tabs.expenditure'), icon: CreditCard },
    ...(isGlobalAdmin ? [{ id: 'settlement', label: t('budget.tabs.settlement'), icon: CheckSquare }] : []),
    ...(isGlobalAdmin ? [{ id: 'settings', label: t('budget.tabs.settings'), icon: Settings }] : [])
  ];

  useEffect(() => {
    if (activeSchoolId) {
      if (globalSchoolYear) {
        if (activeTab === 'expenditure') {
          fetchExpenditures();
        } else if (activeTab === 'budget') {
          fetchBudgets();
        } else if (activeTab === 'statistics') {
          fetchBudgets();
          fetchExpenditures();
        }
      }
      fetchDropdownData();
    }
  }, [activeSchoolId, activeTab, globalSchoolYear]);

  const fetchDropdownData = async () => {
    if (!activeSchoolId) return;
    try {
      // 1. Academic Years
      const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
      const yList = [];
      ySnap.forEach(d => {
        const data = d.data();
        yList.push(data.yearName || data.yearId);
      });
      setAcademicYears(yList.sort().reverse());

      // 2. Staff Profiles
      const currentUserEmail = auth.currentUser?.email?.toLowerCase();
      if (currentUserEmail) {
        let accountId = currentUserEmail;
        const indexDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'email_index', accountId));
        if (indexDoc.exists() && indexDoc.data().accountId) {
          accountId = indexDoc.data().accountId;
        }
        let sysRole = null;
        const staffDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'staff_profiles', accountId));
        if (staffDoc.exists()) {
          sysRole = staffDoc.data().role;
        } else {
          const studentDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'student_profiles', accountId));
          if (studentDoc.exists()) {
            sysRole = studentDoc.data().role;
          }
        }
        if (sysRole) {
          setCurrentUserSysRole(sysRole);
        }
      }

      let localInitial = globalInitial;
      if (!localInitial) {
        const sSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
        const sList = [];
        const currentUserEmail = auth.currentUser?.email?.toLowerCase();

        sSnap.forEach(d => {
          const data = d.data();
          if (data.initial) {
            sList.push(data.initial);
            if (currentUserEmail && (data.email?.toLowerCase() === currentUserEmail || d.id.toLowerCase() === currentUserEmail)) {
              localInitial = data.initial;
            }
          }
        });
        setStaffs(sList.sort());
        setCurrentUserGlobal(localInitial, currentUserEmail);
        setCurrentUserInitial(localInitial);
      } else {
        const sSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
        const sList = [];
        sSnap.forEach(d => {
          const data = d.data();
          if (data.initial) sList.push(data.initial);
        });
        setStaffs(sList.sort());
      }

      // 3. System Settings (for Fund Code and Dept Code)
      let sysSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
      if (!sysSnap.exists()) {
        sysSnap = await getDoc(doc(db, 'metadata', 'system_settings'));
      }
      if (sysSnap.exists()) {
        const data = sysSnap.data().dropdowns || {};
        
        const fCodes = (data.sourceOfFunding || []).filter(item => item.active !== false).sort((a,b) => a.code.localeCompare(b.code));
        setFundCodes(fCodes);
        
        const dCodes = (data.departments || []).filter(item => item.active !== false).sort((a,b) => a.code.localeCompare(b.code));
        const cCodes = (data.inventoryCommittee || []).filter(item => item.active !== false).sort((a,b) => a.code.localeCompare(b.code));
        setDeptCodes({ subjects: dCodes, committees: cCodes });
      }

      // 4. Budget Control Settings
      const settingsSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'budget_control_settings'));
      if (settingsSnap.exists()) {
        setBudgetSettings(settingsSnap.data());
      } else {
        setBudgetSettings(null);
      }

      // 5. User Duties
      if (localInitial) {
        const adminDutySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'admin_duty'), where('initial', '==', localInitial)));
        const cDuties = [];
        adminDutySnap.forEach(d => cDuties.push(d.data()));
        
        const teachDutySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'teach_duty'), where('initial', '==', localInitial)));
        const dDuties = [];
        teachDutySnap.forEach(d => dDuties.push(d.data()));
        
        setUserDuties({ committees: cDuties, departments: dDuties });
      }
      setSettingsLoading(false);

      setFormData(prev => ({
        ...prev,
        tic: prev.tic || localInitial,
        schYr: prev.schYr || (yList.length > 0 ? yList[0] : '')
      }));

      setBudgetFormData(prev => ({
        ...prev,
        schYr: prev.schYr || (yList.length > 0 ? yList[0] : '')
      }));

    } catch (e) {
      console.error('Error fetching dropdown data', e);
    }
  };

  const logAction = async (type, recordId, action, changes) => {
    if (!activeSchoolId) return;
    try {
      const logData = {
        logId: `LOG-${Date.now().toString().slice(-6)}`,
        type,
        recordId,
        doa: new Date().toISOString(),
        action,
        changes,
        executor: currentUserInitial || auth.currentUser?.email || 'Unknown'
      };
      await addDoc(collection(db, 'schools', activeSchoolId, 'budget_control_logs'), { ...logData, ...ownerStamp() });
    } catch (err) {
      console.error("Failed to log action", err);
    }
  };

  const fetchExpenditures = async () => {
    if (!activeSchoolId || !globalSchoolYear) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'budget_expenditures'), where('schYr', '==', globalSchoolYear)));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const yrA = a.schYr || ''; const yrB = b.schYr || '';
        if (yrA !== yrB) return yrB.localeCompare(yrA);
        const deptA = a.deptCode || ''; const deptB = b.deptCode || '';
        if (deptA !== deptB) return deptA.localeCompare(deptB);
        return (a.fundCode || '').localeCompare(b.fundCode || '');
      });
      setExpenditures(list);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const fetchBudgets = async () => {
    if (!activeSchoolId || !globalSchoolYear) return;
    setBudgetLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'budget_items'), where('schYr', '==', globalSchoolYear)));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const yrA = a.schYr || ''; const yrB = b.schYr || '';
        if (yrA !== yrB) return yrB.localeCompare(yrA);
        const deptA = a.deptCode || ''; const deptB = b.deptCode || '';
        if (deptA !== deptB) return deptA.localeCompare(deptB);
        return (a.fundCode || '').localeCompare(b.fundCode || '');
      });
      setBudgets(list);
    } catch (e) {
      console.error(e);
    }
    setBudgetLoading(false);
  };

  const handleEdit = (exp) => {
    setEditingExpId(exp.id);
    setFormData({
      expId: exp.expId || '',
      schYr: exp.schYr || '',
      invDate: exp.invDate || '',
      deptCode: exp.deptCode || '',
      item: exp.item || '',
      tic: exp.tic || '',
      fundCode: exp.fundCode || '',
      amt: exp.amt || '',
      settlementMethod: exp.settlementMethod || '',
      remarks: exp.remarks || '',
      invoiceDoc: exp.invoiceDoc || '',
      settled: exp.settled || '',
      transNo: exp.transNo || '',
      receiptDoc: exp.receiptDoc || ''
    });
    setShowAddForm(true);
  };

  const handleEditBudget = (bg) => {
    setEditingBudgetId(bg.id);
    setBudgetFormData({
      budgetId: bg.budgetId || '',
      schYr: bg.schYr || '',
      deptCode: bg.deptCode || '',
      item: bg.item || '',
      fundCode: bg.fundCode || '',
      amt: bg.amt || '',
      status: bg.status || 'Applied'
    });
    setShowBudgetAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('budget.deleteRecordConfirm'))) return;
    try {
      const record = expenditures.find(f => f.id === id);
      if (record && !isGlobalAdmin && (record.settled === 'Settled' || record.settled === 'Rejected')) {
        alert(t('budget.cannotDeleteSettled'));
        return;
      }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'budget_expenditures', id));
      if (record) {
        await logAction('expenditure', record.expId, 'delete', [{ field: 'all', prev: 'Record deleted', newv: '' }]);
      }
      fetchExpenditures();
    } catch (e) {
      alert(t('budget.deleteFailed', { message: e.message }));
    }
  };

  const handleDeleteBudget = async (id) => {
    if (!window.confirm(t('budget.deleteBudgetConfirm'))) return;
    try {
      const record = budgets.find(b => b.id === id);
      if (record && !isGlobalAdmin && record.status === 'Approved') {
        alert(t('budget.cannotDeleteApproved'));
        return;
      }
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'budget_items', id));
      if (record) {
        await logAction('budget', record.budgetId, 'delete', [{ field: 'all', prev: 'Record deleted', newv: '' }]);
      }
      fetchBudgets();
    } catch (e) {
      alert(t('budget.deleteFailed', { message: e.message }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!activeSchoolId) return;
    
    try {
      const dataToSave = { ...formData, schoolId: activeSchoolId };
      let actionType = 'create';
      let changes = [];

      if (!dataToSave.expId) {
        dataToSave.expId = `EXP-${Date.now().toString().slice(-6)}`;
      }

      if (editingExpId) {
        const record = expenditures.find(f => f.id === editingExpId);
        if (record && !isGlobalAdmin && (record.settled === 'Settled' || record.settled === 'Rejected')) {
          alert(t('budget.cannotEditSettled'));
          return;
        }
        actionType = 'update';
        dataToSave.last_updated_at = new Date().toISOString();
        dataToSave.last_updated_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
        const oldRecord = expenditures.find(f => f.id === editingExpId);
        if (oldRecord) {
          dataToSave.settled = oldRecord.settled || 'Pending';
          for (const key of Object.keys(dataToSave)) {
            if (dataToSave[key] !== oldRecord[key] && key !== 'schoolId' && key !== 'last_updated_at' && key !== 'last_updated_by') {
              changes.push({ field: key, prev: oldRecord[key], newv: dataToSave[key] });
            }
          }
        }
        await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_expenditures', editingExpId), dataToSave);
      } else {
        dataToSave.created_at = new Date().toISOString();
        dataToSave.created_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
        dataToSave.last_updated_at = dataToSave.created_at;
        dataToSave.last_updated_by = dataToSave.created_by;
        dataToSave.settled = 'Pending';
        changes = [{ field: 'all', prev: '', newv: 'Created record' }];
        await addDoc(collection(db, 'schools', activeSchoolId, 'budget_expenditures'), { ...dataToSave, ...ownerStamp() });
      }
      
      if (changes.length > 0) {
        await logAction('expenditure', dataToSave.expId, actionType, changes);
      }
      
      const nextSchYr = formData.schYr;
      const nextTic = formData.tic;
      setFormData({...initialFormState, schYr: nextSchYr, tic: nextTic});
      setShowAddForm(false);
      setEditingExpId(null);
      fetchExpenditures();
    } catch (e) {
      alert(t('budget.saveFailed', { message: e.message }));
    }
  };

  const handleEditSettlement = (exp) => {
    setEditingSettlementId(exp.id);
    setSettlementFormData({
      settled: exp.settled || 'Pending',
      settlementMethod: exp.settlementMethod || '',
      transNo: exp.transNo || '',
      invoiceDoc: exp.invoiceDoc || '',
      receiptDoc: exp.receiptDoc || '',
      remarks: exp.remarks || ''
    });
  };

  const handleCancelSettlement = () => {
    setEditingSettlementId(null);
    setSettlementFormData({});
  };

  const handleSaveSettlement = async (expId) => {
    try {
      const dataToSave = {
        settled: settlementFormData.settled,
        settlementMethod: settlementFormData.settlementMethod,
        transNo: settlementFormData.transNo,
        invoiceDoc: settlementFormData.invoiceDoc,
        receiptDoc: settlementFormData.receiptDoc,
        remarks: settlementFormData.remarks,
        last_updated_at: new Date().toISOString(),
        last_updated_by: currentUserInitial || auth.currentUser?.email || 'Unknown'
      };
      
      const oldRecord = expenditures.find(f => f.id === expId);
      const changes = [];
      if (oldRecord) {
        for (const key of Object.keys(dataToSave)) {
          if (dataToSave[key] !== oldRecord[key] && key !== 'last_updated_at' && key !== 'last_updated_by') {
            changes.push({ field: key, prev: oldRecord[key], newv: dataToSave[key] });
          }
        }
      }
      
      await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_expenditures', expId), dataToSave);
      
      if (changes.length > 0) {
        logAction('update', 'Expenditure Settlement', expId, changes);
      }
      
      setEditingSettlementId(null);
      fetchExpenditures();
    } catch (e) {
      alert(t('budget.saveSettlementFailed', { message: e.message }));
    }
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();
    if (!activeSchoolId) return;
    
    try {
      const dataToSave = { ...budgetFormData, schoolId: activeSchoolId };
      let actionType = 'create';
      let changes = [];

      if (!dataToSave.budgetId) {
        dataToSave.budgetId = `BGT-${Date.now().toString().slice(-6)}`;
      }

      if (editingBudgetId) {
        const record = budgets.find(b => b.id === editingBudgetId);
        if (record && !isGlobalAdmin && record.status === 'Approved') {
          alert(t('budget.cannotEditApproved'));
          return;
        }
        actionType = 'update';
        dataToSave.last_updated_at = new Date().toISOString();
        dataToSave.last_updated_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
        const oldRecord = budgets.find(b => b.id === editingBudgetId);
        if (oldRecord) {
          for (const key of Object.keys(dataToSave)) {
            if (dataToSave[key] !== oldRecord[key] && key !== 'schoolId' && key !== 'last_updated_at' && key !== 'last_updated_by') {
              changes.push({ field: key, prev: oldRecord[key], newv: dataToSave[key] });
            }
          }
        }
        await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_items', editingBudgetId), dataToSave);
      } else {
        dataToSave.created_at = new Date().toISOString();
        dataToSave.created_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
        dataToSave.last_updated_at = dataToSave.created_at;
        dataToSave.last_updated_by = dataToSave.created_by;
        changes = [{ field: 'all', prev: '', newv: 'Created record' }];
        await addDoc(collection(db, 'schools', activeSchoolId, 'budget_items'), { ...dataToSave, ...ownerStamp() });
      }
      
      if (changes.length > 0) {
        await logAction('budget', dataToSave.budgetId, actionType, changes);
      }
      
      const nextSchYr = budgetFormData.schYr;
      setBudgetFormData({...initialBudgetFormState, schYr: nextSchYr});
      setShowBudgetAddForm(false);
      setEditingBudgetId(null);
      fetchBudgets();
    } catch (e) {
      alert(t('budget.saveBudgetFailed', { message: e.message }));
    }
  };

  const handleExportCSV = () => {
    const csvData = expenditures.map(f => ({
      'EXP_ID': f.expId || '',
      'School Year': f.schYr || '',
      'Invoice Date': f.invDate || '',
      'Dept Code': f.deptCode || '',
      'Item': f.item || '',
      'TIC': f.tic || '',
      'Fund Code': f.fundCode || '',
      'Amount': f.amt || '',
      'Settlement Method': f.settlementMethod || '',
      'Remarks': f.remarks || '',
      'Invoice Doc': f.invoiceDoc || '',
      'Status': f.settled || '',
      'Transaction No': f.transNo || '',
      'Receipt Doc': f.receiptDoc || '',
      'Created By': f.created_by || '',
      'Created At': formatDateTime(f.created_at) || '',
      'Updated By': f.last_updated_by || '',
      'Updated At': formatDateTime(f.last_updated_at) || ''
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Expenditures_Export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportBudgetCSV = () => {
    const csvData = budgets.map(b => ({
      'BUDGET_ID': b.budgetId || '',
      'School Year': b.schYr || '',
      'Dept Code': b.deptCode || '',
      'Item': b.item || '',
      'Fund Code': b.fundCode || '',
      'Amount': b.amt || '',
      'Status': b.status || '',
      'Created By': b.created_by || '',
      'Created At': formatDateTime(b.created_at) || '',
      'Updated By': b.last_updated_by || '',
      'Updated At': formatDateTime(b.last_updated_at) || ''
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Budgets_Export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedData = results.data;
        let successCount = 0;
        let errorCount = 0;

        const validFundCodes = fundCodes.map(f => f.code);
        const validDeptCodes = [...deptCodes.subjects.map(c => c.code), ...deptCodes.committees.map(c => c.code)];
        const validExpStatuses = ['Settled', 'Pending', ''];

        for (let i = 0; i < parsedData.length; i++) {
          const row = parsedData[i];
          const fCode = row['Fund Code']?.trim() || '';
          const dCode = row['Dept Code']?.trim() || '';
          const status = row['Status']?.trim() || '';

          if (!validFundCodes.includes(fCode)) {
            alert(t('budget.badFundCode', { row: i + 2, value: fCode }));
            e.target.value = null;
            return;
          }
          if (!validDeptCodes.includes(dCode)) {
            alert(t('budget.badDeptCode', { row: i + 2, value: dCode }));
            e.target.value = null;
            return;
          }
          if (!hasAccessToRecord(dCode, 'Expenditure', 'Edit')) {
            alert(t('budget.noEditPermission', { row: i + 2, value: dCode }));
            e.target.value = null;
            return;
          }
          if (status && !validExpStatuses.includes(status)) {
            alert(t('budget.badStatus', { row: i + 2, value: status }));
            e.target.value = null;
            return;
          }
        }

        if (parsedData.length > 0) {
          setImportLoading(true);
        }

        for (const row of parsedData) {
          let expId = row['EXP_ID']?.trim();
          if (!expId) {
            expId = `EXP-${Date.now().toString().slice(-6)}-${Math.floor(Math.random()*1000)}`;
          }

          let expData = {
            schoolId: activeSchoolId,
            expId: expId,
            schYr: row['School Year']?.trim() || '',
            invDate: row['Invoice Date']?.trim() || '',
            deptCode: row['Dept Code']?.trim() || '',
            item: row['Item']?.trim() || '',
            tic: row['TIC']?.trim() || '',
            fundCode: row['Fund Code']?.trim() || '',
            amt: row['Amount']?.trim() || '',
            settlementMethod: row['Settlement Method']?.trim() || '',
            remarks: row['Remarks']?.trim() || '',
            invoiceDoc: row['Invoice Doc']?.trim() || '',
            transNo: row['Transaction No']?.trim() || '',
            receiptDoc: row['Receipt Doc']?.trim() || ''
          };

          try {
            const existingExp = expenditures.find(f => f.expId === expId);
            if (existingExp) {
              if (!isGlobalAdmin && (existingExp.settled === 'Settled' || existingExp.settled === 'Rejected')) {
                errorCount++;
                continue;
              }
              expData.created_by = existingExp.created_by || 'Unknown';
              expData.created_at = existingExp.created_at || new Date().toISOString();
              expData.last_updated_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
              expData.last_updated_at = new Date().toISOString();
              expData.settled = existingExp.settled || 'Pending';

              await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_expenditures', existingExp.id), expData);
            } else {
              expData.created_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
              expData.created_at = new Date().toISOString();
              expData.last_updated_by = expData.created_by;
              expData.last_updated_at = expData.created_at;
              expData.settled = 'Pending';

              await addDoc(collection(db, 'schools', activeSchoolId, 'budget_expenditures'), { ...expData, ...ownerStamp() });
            }
            successCount++;
          } catch (err) {
            console.error("Error importing row:", row, err);
            errorCount++;
          }
        }
        alert(`${t('budget.importComplete', { ok: successCount, failed: errorCount })}\n\n${t('budget.systemFieldsNote')}`);
        setImportLoading(false);
        fetchExpenditures();
        e.target.value = null;
      },
      error: (err) => {
        alert(t('budget.parseFailed', { message: err.message }));
      }
    });
  };

  const handleImportBudgetCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedData = results.data;
        let successCount = 0;
        let errorCount = 0;

        const validFundCodes = fundCodes.map(f => f.code);
        const validDeptCodes = [...deptCodes.subjects.map(c => c.code), ...deptCodes.committees.map(c => c.code)];
        const validBudgetStatuses = ['Applied', 'Approved', 'Rejected', ''];

        for (let i = 0; i < parsedData.length; i++) {
          const row = parsedData[i];
          const fCode = row['Fund Code']?.trim() || '';
          const dCode = row['Dept Code']?.trim() || '';
          const status = row['Status']?.trim() || '';

          if (!validFundCodes.includes(fCode)) {
            alert(t('budget.badFundCode', { row: i + 2, value: fCode }));
            e.target.value = null;
            return;
          }
          if (!validDeptCodes.includes(dCode)) {
            alert(t('budget.badDeptCode', { row: i + 2, value: dCode }));
            e.target.value = null;
            return;
          }
          if (!hasAccessToRecord(dCode, 'Budget', 'Edit')) {
            alert(t('budget.noEditPermission', { row: i + 2, value: dCode }));
            e.target.value = null;
            return;
          }
          if (status && !validBudgetStatuses.includes(status)) {
            alert(t('budget.badStatus', { row: i + 2, value: status }));
            e.target.value = null;
            return;
          }
        }

        if (parsedData.length > 0) {
          setImportLoading(true);
        }

        for (const row of parsedData) {
          let bgtId = row['BUDGET_ID']?.trim();
          if (!bgtId) {
            bgtId = `BGT-${Date.now().toString().slice(-6)}-${Math.floor(Math.random()*1000)}`;
          }

          let bgtData = {
            schoolId: activeSchoolId,
            budgetId: bgtId,
            schYr: row['School Year']?.trim() || '',
            deptCode: row['Dept Code']?.trim() || '',
            item: row['Item']?.trim() || '',
            fundCode: row['Fund Code']?.trim() || '',
            amt: row['Amount']?.trim() || ''
          };

          try {
            const existingBgt = budgets.find(b => b.budgetId === bgtId);
          if (existingBgt) {
            if (!isGlobalAdmin && existingBgt.status === 'Approved') {
              errorCount++;
              continue;
            }
            bgtData.status = existingBgt.status || 'Applied';
              bgtData.created_by = existingBgt.created_by || 'Unknown';
              bgtData.created_at = existingBgt.created_at || new Date().toISOString();
              bgtData.last_updated_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
              bgtData.last_updated_at = new Date().toISOString();

              await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_items', existingBgt.id), bgtData);
            } else {
              bgtData.status = 'Applied';
              bgtData.created_by = currentUserInitial || auth.currentUser?.email || 'Unknown';
              bgtData.created_at = new Date().toISOString();
              bgtData.last_updated_by = bgtData.created_by;
              bgtData.last_updated_at = bgtData.created_at;

              await addDoc(collection(db, 'schools', activeSchoolId, 'budget_items'), { ...bgtData, ...ownerStamp() });
            }
            successCount++;
          } catch (err) {
            console.error("Error importing budget row:", row, err);
            errorCount++;
          }
        }
        alert(`${t('budget.budgetImportComplete', { ok: successCount, failed: errorCount })}\n\n${t('budget.systemFieldsNote')}`);
        setImportLoading(false);
        fetchBudgets();
        e.target.value = null;
      },
      error: (err) => {
        alert(t('budget.parseFailed', { message: err.message }));
      }
    });
  };

  const handleExportLogCSV = async () => {
    if (!activeSchoolId) return;
    try {
      setLogsLoading(true);
      
      let logsRef = collection(db, 'schools', activeSchoolId, 'budget_control_logs');
      const snap = await getDocs(query(logsRef));
      
      let results = [];
      snap.forEach(d => {
        results.push(d.data());
      });
      
      results = results.filter(log => {
        if (logTypeFilter !== 'both' && log.type !== logTypeFilter) return false;
        if (logStartDate) {
          const logDate = log.doa.split('T')[0];
          if (logDate < logStartDate) return false;
        }
        if (logEndDate) {
          const logDate = log.doa.split('T')[0];
          if (logDate > logEndDate) return false;
        }
        return true;
      });
      
      results.sort((a,b) => new Date(b.doa) - new Date(a.doa));
      
      if (results.length === 0) {
        alert(t('budget.noLogs'));
        setLogsLoading(false);
        return;
      }
      
      const csvData = results.map(log => {
        let changesStr = '';
        if (log.changes && Array.isArray(log.changes)) {
          changesStr = log.changes.map(c => `field: ${c.field}, type: ${log.action}, prev: ${c.prev}, newv: ${c.newv}`).join(' | ');
        }
        return {
          'Log ID': log.logId || '',
          'Record ID': log.recordId || '',
          'Date of Action': formatDateTime(log.doa) || '',
          'Action': log.action || '',
          'Type': log.type || '',
          'Executor': log.executor || '',
          'Changes': changesStr
        };
      });
      
      const csv = Papa.unparse(csvData);
      const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Budget_Expenditure_Logs.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert(t('budget.exportLogsFailed', { message: e.message }));
    } finally {
      setLogsLoading(false);
    }
  };

  const filteredExpenditures = React.useMemo(() => {
    let result = expenditures.filter(f => hasAccessToRecord(f.deptCode, 'Expenditure', 'View'));
    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      result = result.filter(f => 
        (f.expId || '').toLowerCase().includes(needle) ||
        (f.item || '').toLowerCase().includes(needle) ||
        (f.deptCode || '').toLowerCase().includes(needle) ||
        (f.fundCode || '').toLowerCase().includes(needle) ||
        (f.tic || '').toLowerCase().includes(needle) ||
        (f.created_by || '').toLowerCase().includes(needle) ||
        (f.last_updated_by || '').toLowerCase().includes(needle)
      );
    }
    Object.keys(filterConfig).forEach(key => {
      if (filterConfig[key] && filterConfig[key].length > 0) {
        result = result.filter(f => filterConfig[key].includes(f[key] || ''));
      }
    });
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let valA = a[sortConfig.key] || '';
        let valB = b[sortConfig.key] || '';
        if (sortConfig.key === 'amt') {
          valA = parseFloat(valA.toString().replace(/[^0-9.-]+/g,"")) || 0;
          valB = parseFloat(valB.toString().replace(/[^0-9.-]+/g,"")) || 0;
        }
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [expenditures, searchTerm, filterConfig, sortConfig, hasAccessToRecord]);

  const filteredBudgets = React.useMemo(() => {
    let result = budgets.filter(b => hasAccessToRecord(b.deptCode, 'Budget', 'View'));
    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      result = result.filter(b => 
        (b.budgetId || '').toLowerCase().includes(needle) ||
        (b.item || '').toLowerCase().includes(needle) ||
        (b.deptCode || '').toLowerCase().includes(needle) ||
        (b.fundCode || '').toLowerCase().includes(needle) ||
        (b.tic || '').toLowerCase().includes(needle) ||
        (b.created_by || '').toLowerCase().includes(needle) ||
        (b.last_updated_by || '').toLowerCase().includes(needle)
      );
    }
    Object.keys(filterConfig).forEach(key => {
      if (filterConfig[key] && filterConfig[key].length > 0) {
        result = result.filter(b => filterConfig[key].includes(b[key] || ''));
      }
    });
    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let valA = a[sortConfig.key] || '';
        let valB = b[sortConfig.key] || '';
        if (sortConfig.key === 'amt') {
          valA = parseFloat(valA.toString().replace(/[^0-9.-]+/g,"")) || 0;
          valB = parseFloat(valB.toString().replace(/[^0-9.-]+/g,"")) || 0;
        }
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [budgets, searchTerm, filterConfig, sortConfig, hasAccessToRecord]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterConfig, sortConfig, activeTab]);

  const paginatedExpenditures = React.useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredExpenditures.slice(start, start + recordsPerPage);
  }, [filteredExpenditures, currentPage, recordsPerPage]);

  const paginatedBudgets = React.useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filteredBudgets.slice(start, start + recordsPerPage);
  }, [filteredBudgets, currentPage, recordsPerPage]);

  const handleBatchDelete = async () => {
    if (activeTab === 'expenditure') {
      if (selectedExpenditures.length === 0) return;
      if (!window.confirm(t('budget.deleteExpendituresConfirm', { count: selectedExpenditures.length }))) return;
      setBatchActionLoading(true);
      try {
        for (const id of selectedExpenditures) {
          const record = expenditures.find(f => f.id === id);
          if (record) {
            if (!isGlobalAdmin && (record.settled === 'Settled' || record.settled === 'Rejected')) continue;
            await deleteDoc(doc(db, 'schools', activeSchoolId, 'budget_expenditures', id));
            await logAction('expenditure', record.expId, 'delete', [{ field: 'all', prev: 'record existed', newv: 'deleted' }]);
          }
        }
        setSelectedExpenditures([]);
        await fetchExpenditures();
      } catch (err) {
        console.error("Batch delete error:", err);
      } finally {
        setBatchActionLoading(false);
      }
    } else if (activeTab === 'budget') {
      if (selectedBudgets.length === 0) return;
      if (!window.confirm(t('budget.deleteBudgetsConfirm', { count: selectedBudgets.length }))) return;
      setBatchActionLoading(true);
      try {
        for (const id of selectedBudgets) {
          const record = budgets.find(b => b.id === id);
          if (record) {
            if (!isGlobalAdmin && record.status === 'Approved') continue;
            await deleteDoc(doc(db, 'schools', activeSchoolId, 'budget_items', id));
            await logAction('budget', record.budgetId, 'delete', [{ field: 'all', prev: 'record existed', newv: 'deleted' }]);
          }
        }
        setSelectedBudgets([]);
        await fetchBudgets();
      } catch (err) {
        console.error("Batch delete error:", err);
      } finally {
        setBatchActionLoading(false);
      }
    }
  };

  const handleBatchUpdate = async () => {
    if (!batchUpdateField || !batchUpdateValue) return;
    if (activeTab === 'expenditure') {
      if (selectedExpenditures.length === 0) return;
      if (!window.confirm(t('budget.batchUpdateExpenditures', { field: batchUpdateField, value: batchUpdateValue, count: selectedExpenditures.length }))) return;
      setBatchActionLoading(true);
      try {
        for (const id of selectedExpenditures) {
          const record = expenditures.find(f => f.id === id);
          if (record && record[batchUpdateField] !== batchUpdateValue) {
            if (!isGlobalAdmin && (record.settled === 'Settled' || record.settled === 'Rejected')) continue;
            await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_expenditures', id), {
              [batchUpdateField]: batchUpdateValue,
              last_updated_by: currentUserInitial || auth.currentUser?.email || 'Unknown',
              last_updated_at: new Date().toISOString()
            });
            await logAction('expenditure', record.expId, 'update', [{ field: batchUpdateField, prev: record[batchUpdateField], newv: batchUpdateValue }]);
          }
        }
        setSelectedExpenditures([]);
        setBatchUpdateField('');
        setBatchUpdateValue('');
        await fetchExpenditures();
      } catch (err) {
        console.error("Batch update error:", err);
      } finally {
        setBatchActionLoading(false);
      }
    } else if (activeTab === 'budget') {
      if (selectedBudgets.length === 0) return;
      if (!window.confirm(t('budget.batchUpdateBudgets', { field: batchUpdateField, value: batchUpdateValue, count: selectedBudgets.length }))) return;
      setBatchActionLoading(true);
      try {
        for (const id of selectedBudgets) {
          const record = budgets.find(b => b.id === id);
          if (record && record[batchUpdateField] !== batchUpdateValue) {
            if (!isGlobalAdmin && record.status === 'Approved') continue;
            await updateDoc(doc(db, 'schools', activeSchoolId, 'budget_items', id), {
              [batchUpdateField]: batchUpdateValue,
              last_updated_by: currentUserInitial || auth.currentUser?.email || 'Unknown',
              last_updated_at: new Date().toISOString()
            });
            await logAction('budget', record.budgetId, 'update', [{ field: batchUpdateField, prev: record[batchUpdateField], newv: batchUpdateValue }]);
          }
        }
        setSelectedBudgets([]);
        setBatchUpdateField('');
        setBatchUpdateValue('');
        await fetchBudgets();
      } catch (err) {
        console.error("Batch update error:", err);
      } finally {
        setBatchActionLoading(false);
      }
    }
  };

  const toggleSelectAll = () => {
    if (activeTab === 'expenditure') {
      if (selectedExpenditures.length === filteredExpenditures.length && filteredExpenditures.length > 0) {
        setSelectedExpenditures([]);
      } else {
        setSelectedExpenditures(filteredExpenditures.map(f => f.id));
      }
    } else if (activeTab === 'budget') {
      if (selectedBudgets.length === filteredBudgets.length && filteredBudgets.length > 0) {
        setSelectedBudgets([]);
      } else {
        setSelectedBudgets(filteredBudgets.map(b => b.id));
      }
    }
  };

  const toggleSelectRow = (id) => {
    if (activeTab === 'expenditure') {
      setSelectedExpenditures(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else if (activeTab === 'budget') {
      setSelectedBudgets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    }
  };
  const currentFilteredDataLength = activeTab === 'expenditure' ? filteredExpenditures.length : filteredBudgets.length;
  const totalPages = Math.ceil(currentFilteredDataLength / recordsPerPage) || 1;

  return (
    <>
      {(batchActionLoading || importLoading) && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          zIndex: 99999, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', backgroundColor: 'var(--color-bg-card)', padding: '1.5rem 3rem', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-xl)', border: '1px solid var(--color-border)' }}>
            <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
            </svg>
            <span style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--color-text)' }}>
              {importLoading ? 'Importing records... Please wait.' : 'Processing Batch Request...'}
            </span>
          </div>
        </div>
      )}
      <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Tabs */}
        <div className="theme-tabs-bar">
          <div className="theme-tabs-group">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`theme-tab ${isActive ? 'active' : ''}`}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <div className="theme-tab-icon">
                    <Icon size={18} />
                  </div>
                  {tab.label}
                </button>
              )
            })}
          </div>

          <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {(activeTab === 'expenditure' || activeTab === 'budget') && (
              <input 
                type="text" 
                className="input-field" 
                placeholder={t('budget.searchRecords')} 
                value={searchTerm} 
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '200px', padding: '0.4rem 0.75rem', height: '36px' }}
              />
            )}

            {activeTab === 'expenditure' && hasEditAccessToAny('Expenditure') && (
              <>
                <button onClick={handleExportCSV} title={t('budget.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', whiteSpace: 'nowrap' }}>
                  <Download size={16} />
                </button>
                <label title={t('budget.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', margin: 0, whiteSpace: 'nowrap' }}>
                  <Upload size={16} />
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
                </label>
                <button className="btn-primary" onClick={() => { setShowAddForm(true); setEditingExpId(null); setFormData({...initialFormState, tic: formData.tic, schYr: formData.schYr}); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: 'auto', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {t('budget.newRecord')}
                </button>
              </>
            )}

            {activeTab === 'budget' && hasEditAccessToAny('Budget') && (
              <>
                <button onClick={handleExportBudgetCSV} title={t('budget.exportBudgetCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', whiteSpace: 'nowrap' }}>
                  <Download size={16} />
                </button>
                <label title={t('budget.importBudgetCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', margin: 0, whiteSpace: 'nowrap' }}>
                  <Upload size={16} />
                  <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportBudgetCSV} />
                </label>
                <button className="btn-primary" onClick={() => { setShowBudgetAddForm(true); setEditingBudgetId(null); setBudgetFormData({...initialBudgetFormState, schYr: budgetFormData.schYr}); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: 'auto', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {t('budget.newRecord')}
                </button>
              </>
            )}

            <select 
              className="input-field" 
              value={globalSchoolYear} 
              onChange={e => setGlobalSchoolYear(e.target.value)}
              style={{ width: '130px', padding: '0.4rem 0.75rem', height: '36px', marginLeft: '0.5rem' }}
            >
              {academicYears.map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
          </HeaderActionsPortal>
        </div>

      {showAddForm && activeTab === 'expenditure' && (
        <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-md)', border: '1px solid var(--color-border)', marginTop: '1rem' }}>
          <form onSubmit={handleSave}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{editingExpId ? 'Edit Expenditure Record' : 'Add Expenditure Record'}</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setShowAddForm(false)} style={{ width: 'auto', padding: '0.4rem 1rem' }}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.4rem 1rem' }}>{t('common.save')}</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.expId')}</label>
              <input type="text" className="input-field" placeholder={t('budget.autoAssigned')} value={formData.expId} onChange={e => setFormData({...formData, expId: e.target.value})} />
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('common.schoolYear')}</label>
              <select className="input-field" value={formData.schYr} onChange={e => setFormData({...formData, schYr: e.target.value})} required>
                <option value="" disabled>{t('budget.selectYear')}</option>
                {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.invoiceDate')}</label>
              <input type="date" className="input-field" value={formData.invDate} onChange={e => setFormData({...formData, invDate: e.target.value})} required />
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.deptCode')}</label>
              <select className="input-field" value={formData.deptCode} onChange={e => setFormData({...formData, deptCode: e.target.value})} required>
                <option value="" disabled>{t('budget.selectDept')}</option>
                <option value="" disabled>{t('budget.committeeGroup')}</option>
                {(deptCodes.committees || []).filter(d => hasAccessToRecord(d.code, 'Expenditure', 'Edit')).map(d => <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>)}
                <option value="" disabled>{t('budget.subjectsGroup')}</option>
                {(deptCodes.subjects || []).filter(d => hasAccessToRecord(d.code, 'Expenditure', 'Edit')).map(d => <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 400px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.itemDescription')}</label>
              <input type="text" className="input-field" value={formData.item} onChange={e => setFormData({...formData, item: e.target.value})} required />
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.tic')}</label>
              <select className="input-field" value={formData.tic} onChange={e => setFormData({...formData, tic: e.target.value})} required>
                <option value="" disabled>{t('budget.selectTic')}</option>
                {staffs.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.fundCode')}</label>
              <select className="input-field" value={formData.fundCode} onChange={e => setFormData({...formData, fundCode: e.target.value})} required>
                <option value="" disabled>{t('budget.selectFund')}</option>
                {fundCodes.map(f => <option key={f.code} value={f.code}>{f.code} - {f.nameEn || f.name}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.amount')}</label>
              <input type="number" step="0.01" className="input-field" value={formData.amt} onChange={e => setFormData({...formData, amt: e.target.value})} required />
            </div>

            {/* Note: Settlement Method, Status, Trans No, Remarks, Invoice Doc, and Receipt Doc are hidden here. 
                They will be edited in the Settlement tab by accountants. */}

            </div>
          </form>
        </div>
      )}

      {showBudgetAddForm && activeTab === 'budget' && (
        <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-md)', border: '1px solid var(--color-border)', marginTop: '1rem' }}>
          <form onSubmit={handleSaveBudget}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{editingBudgetId ? 'Edit Budget Record' : 'Apply for Budget'}</h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setShowBudgetAddForm(false)} style={{ width: 'auto', padding: '0.4rem 1rem' }}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.4rem 1rem' }}>{t('common.save')}</button>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.budgetId')}</label>
              <input type="text" className="input-field" placeholder={t('budget.autoAssigned')} value={budgetFormData.budgetId} onChange={e => setBudgetFormData({...budgetFormData, budgetId: e.target.value})} />
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('common.schoolYear')}</label>
              <select className="input-field" value={budgetFormData.schYr} onChange={e => setBudgetFormData({...budgetFormData, schYr: e.target.value})} required>
                <option value="" disabled>{t('budget.selectYear')}</option>
                {academicYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.deptCode')}</label>
              <select className="input-field" value={budgetFormData.deptCode} onChange={e => setBudgetFormData({...budgetFormData, deptCode: e.target.value})} required>
                <option value="" disabled>{t('budget.selectDept')}</option>
                <option value="" disabled>{t('budget.committeeGroup')}</option>
                {(deptCodes.committees || []).filter(d => hasAccessToRecord(d.code, 'Budget', 'Edit')).map(d => <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>)}
                <option value="" disabled>{t('budget.subjectsGroup')}</option>
                {(deptCodes.subjects || []).filter(d => hasAccessToRecord(d.code, 'Budget', 'Edit')).map(d => <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 400px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.itemDescription')}</label>
              <input type="text" className="input-field" value={budgetFormData.item} onChange={e => setBudgetFormData({...budgetFormData, item: e.target.value})} required />
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.fundCode')}</label>
              <select className="input-field" value={budgetFormData.fundCode} onChange={e => setBudgetFormData({...budgetFormData, fundCode: e.target.value})} required>
                <option value="" disabled>{t('budget.selectFund')}</option>
                {fundCodes.map(f => <option key={f.code} value={f.code}>{f.code} - {f.nameEn || f.name}</option>)}
              </select>
            </div>
            
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.amount')}</label>
              <input type="number" step="0.01" className="input-field" value={budgetFormData.amt} onChange={e => setBudgetFormData({...budgetFormData, amt: e.target.value})} required />
            </div>

            </div>
          </form>
        </div>
      )}
      {/* Function Bar */}
      {(activeTab === 'expenditure' || activeTab === 'budget') && (
        <div style={{ marginTop: '1rem', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          {/* Pagination Left */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
              Showing {currentFilteredDataLength > 0 ? (currentPage - 1) * recordsPerPage + 1 : 0} to {Math.min(currentPage * recordsPerPage, currentFilteredDataLength)} of {currentFilteredDataLength} records
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{t('budget.perPage')}</span>
              <select className="input-field" value={recordsPerPage} onChange={e => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} style={{ padding: '0.2rem 0.5rem', height: 'auto', width: 'auto' }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Batch Update Center/Right */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {activeTab === 'expenditure' && selectedExpenditures.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', backgroundColor: 'var(--color-bg-app)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>{selectedExpenditures.length} selected</span>
                <select className="input-field" value={batchUpdateField} onChange={e => { setBatchUpdateField(e.target.value); setBatchUpdateValue(''); }} style={{ padding: '0.25rem', height: 'auto', minWidth: '120px' }}>
                  <option value="" disabled>{t('budget.selectField')}</option>
                  <option value="fundCode">{t('budget.fundCode')}</option>
                  <option value="deptCode">{t('budget.deptCode')}</option>
                  <option value="tic">{t('budget.tic')}</option>
                </select>
                {batchUpdateField && (
                  <select className="input-field" value={batchUpdateValue} onChange={e => setBatchUpdateValue(e.target.value)} style={{ padding: '0.25rem', height: 'auto', minWidth: '120px' }}>
                    <option value="" disabled>{t('budget.selectValue')}</option>
                    {batchUpdateField === 'fundCode' && fundCodes.map(f => <option key={f.code} value={f.code}>{f.code}</option>)}
                    {batchUpdateField === 'deptCode' && [...(deptCodes.committees || []), ...(deptCodes.subjects || [])].filter(d => hasAccessToRecord(d.code, activeTab === 'budget' ? 'Budget' : 'Expenditure', 'Edit')).map(d => <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>)}
                    {batchUpdateField === 'tic' && staffs.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                )}
                <button onClick={handleBatchUpdate} disabled={batchActionLoading || !batchUpdateField || !batchUpdateValue} className="btn-primary" style={{ padding: '0.25rem 0.75rem', height: 'auto', width: 'auto' }}>
                  Update
                </button>
                <button onClick={handleBatchDelete} disabled={batchActionLoading} className="btn-outline" style={{ padding: '0.25rem 0.75rem', height: 'auto', width: 'auto', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                  Delete
                </button>
              </div>
            )}
            
            {activeTab === 'budget' && selectedBudgets.length > 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', backgroundColor: 'var(--color-bg-app)', padding: '0.25rem 0.5rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>{selectedBudgets.length} selected</span>
                <select className="input-field" value={batchUpdateField} onChange={e => { setBatchUpdateField(e.target.value); setBatchUpdateValue(''); }} style={{ padding: '0.25rem', height: 'auto', minWidth: '120px' }}>
                  <option value="" disabled>{t('budget.selectField')}</option>
                  {isGlobalAdmin && <option value="status">{t('common.status')}</option>}
                  <option value="fundCode">{t('budget.fundCode')}</option>
                  <option value="deptCode">{t('budget.deptCode')}</option>
                </select>
                {batchUpdateField && (
                  <select className="input-field" value={batchUpdateValue} onChange={e => setBatchUpdateValue(e.target.value)} style={{ padding: '0.25rem', height: 'auto', minWidth: '120px' }}>
                    <option value="" disabled>{t('budget.selectValue')}</option>
                    {batchUpdateField === 'status' && (
                      <>
                        <option value="Applied">{t('budget.budgetStatuses.applied')}</option>
                        <option value="Approved">{t('budget.budgetStatuses.approved')}</option>
                        <option value="Rejected">{t('budget.budgetStatuses.rejected')}</option>
                      </>
                    )}
                    {batchUpdateField === 'fundCode' && fundCodes.map(f => <option key={f.code} value={f.code}>{f.code}</option>)}
                    {batchUpdateField === 'deptCode' && [...(deptCodes.committees || []), ...(deptCodes.subjects || [])].filter(d => hasAccessToRecord(d.code, activeTab === 'budget' ? 'Budget' : 'Expenditure', 'Edit')).map(d => <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>)}
                  </select>
                )}
                <button onClick={handleBatchUpdate} disabled={batchActionLoading || !batchUpdateField || !batchUpdateValue} className="btn-primary" style={{ padding: '0.25rem 0.75rem', height: 'auto', width: 'auto' }}>
                  Update
                </button>
                <button onClick={handleBatchDelete} disabled={batchActionLoading} className="btn-outline" style={{ padding: '0.25rem 0.75rem', height: 'auto', width: 'auto', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}>
                  Delete
                </button>
              </div>
            )}

            {/* Pagination Right */}
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
              <button className="btn-outline" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{ padding: '0.2rem 0.5rem', height: 'auto', width: 'auto' }}>{t('budget.prev')}</button>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', fontSize: '0.875rem' }}>Page {currentPage} of {totalPages}</div>
              <button className="btn-outline" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages || currentFilteredDataLength === 0} style={{ padding: '0.2rem 0.5rem', height: 'auto', width: 'auto' }}>{t('budget.next')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column', marginTop: '1rem', padding: '0' }}>
        {activeTab === 'statistics' ? (
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--color-text)' }}>{t('budget.statisticsTitle')}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label style={{ fontWeight: '600', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{t('budget.deptCommitteeLabel')}</label>
                  <select 
                    className="input-field" 
                    value={statsDeptCode} 
                    onChange={e => setStatsDeptCode(e.target.value)}
                    style={{ width: '200px' }}
                  >
                    <option value="">{t('common.all')}</option>
                    {[...(deptCodes.committees || []), ...(deptCodes.subjects || [])].filter(d => hasAccessToRecord(d.code, 'Statistics', 'View')).map(d => (
                      <option key={d.code} value={d.code}>{d.code} - {d.nameEn || d.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label style={{ fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('budget.fundingLabel')}</label>
                  <select 
                    className="input-field" 
                    value={statsFundCode} 
                    onChange={e => setStatsFundCode(e.target.value)}
                    style={{ width: '120px' }}
                  >
                    <option value="">{t('common.all')}</option>
                    {fundCodes.map(f => (
                      <option key={f.code} value={f.code}>{f.code}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="table-container" style={{ flex: 1, backgroundColor: 'var(--color-bg-app)', borderRadius: 'var(--radius-lg)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>
                  <tr>
                    <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.deptCommittee')}</th>
                    <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.fundCode')}</th>
                    <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'right' }}>{t('budget.totalBudgetApproved')}</th>
                    <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'right' }}>{t('budget.totalExpenditure')}</th>
                    <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'right' }}>{t('budget.remainingBalance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {statisticsData.length === 0 ? (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
                        No data available for {globalSchoolYear || 'the selected year'}.
                      </td>
                    </tr>
                  ) : (
                    statisticsData.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--color-bg-app)' }}>
                        <td style={{ padding: '1rem', fontWeight: '500' }}>{row.deptCode}</td>
                        <td style={{ padding: '1rem' }}>{row.fundCode}</td>
                        <td style={{ padding: '1rem', textAlign: 'right', color: 'var(--color-primary)', fontWeight: '600' }}>
                          ${row.budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'right', color: 'var(--color-warning)', fontWeight: '600' }}>
                          ${row.expenditure.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '1rem', textAlign: 'right', color: row.remaining < 0 ? 'var(--color-danger)' : 'var(--color-success)', fontWeight: '700' }}>
                          ${row.remaining.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'expenditure' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center', borderBottom: '1px solid var(--color-border)' }}>
                    <input type="checkbox" checked={filteredExpenditures.length > 0 && selectedExpenditures.length === filteredExpenditures.length} onChange={toggleSelectAll} />
                  </th>
                  <SortFilterHeader label={t('budget.expId')} columnKey="expId" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.invDate')} columnKey="invDate" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.deptTic')} columnKey="deptCode" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.item')} columnKey="item" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.fundCode')} columnKey="fundCode" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.amt')} columnKey="amt" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('common.status')} columnKey="settled" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.created')} columnKey="created_at" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.updated')} columnKey="last_updated_at" data={expenditures} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'right' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="11" style={{ textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</td></tr> : 
                 filteredExpenditures.length === 0 ? <tr><td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('budget.noRecords')}</td></tr> :
                 paginatedExpenditures.map(f => (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={selectedExpenditures.includes(f.id)} onChange={() => toggleSelectRow(f.id)} />
                    </td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{f.expId}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{f.invDate}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <div style={{ fontWeight: '600' }}>{f.deptCode}</div>
                      <div style={{ color: 'var(--color-text-muted)' }}>{f.tic}</div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{f.item}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>{f.fundCode}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>${f.amt}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {f.settled ? (
                        <span style={{ 
                          padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                          backgroundColor: f.settled === 'Settled' ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
                          color: f.settled === 'Settled' ? 'var(--color-success-text)' : 'var(--color-warning-text)'
                        }}>
                          {settledWord(t, f.settled)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                      {f.settlementMethod && <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{f.settlementMethod}</div>}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {f.created_by && <div style={{ fontWeight: '600' }}>{f.created_by}</div>}
                      {f.created_at && <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{formatDateTime(f.created_at)}</div>}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {f.last_updated_by && <div style={{ fontWeight: '600' }}>{f.last_updated_by}</div>}
                      {f.last_updated_at && <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{formatDateTime(f.last_updated_at)}</div>}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      {hasAccessToRecord(f.deptCode, 'Expenditure', 'Edit') && (isGlobalAdmin || (f.settled !== 'Settled' && f.settled !== 'Rejected')) && (
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button onClick={() => handleEdit(f)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-primary)', width: 'auto', display: 'inline-flex' }}>
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDelete(f.id)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-danger)', width: 'auto', display: 'inline-flex' }}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'budget' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center', borderBottom: '1px solid var(--color-border)' }}>
                    <input type="checkbox" checked={filteredBudgets.length > 0 && selectedBudgets.length === filteredBudgets.length} onChange={toggleSelectAll} />
                  </th>
                  <SortFilterHeader label={t('budget.budgetId')} columnKey="budgetId" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.deptCode')} columnKey="deptCode" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.item')} columnKey="item" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.fundCode')} columnKey="fundCode" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.amt')} columnKey="amt" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('common.status')} columnKey="status" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.created')} columnKey="created_at" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <SortFilterHeader label={t('budget.updated')} columnKey="last_updated_at" data={budgets} sortConfig={sortConfig} setSortConfig={setSortConfig} filterConfig={filterConfig} setFilterConfig={setFilterConfig} activePopover={activePopover} setActivePopover={setActivePopover} />
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'right' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {budgetLoading ? <tr><td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>{t('common.loading')}</td></tr> : 
                 filteredBudgets.length === 0 ? <tr><td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('budget.noRecords')}</td></tr> :
                 paginatedBudgets.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={selectedBudgets.includes(b.id)} onChange={() => toggleSelectRow(b.id)} />
                    </td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{b.budgetId}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>{b.deptCode}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{b.item}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>{b.fundCode}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>${b.amt}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: b.status === 'Approved' ? 'var(--color-success-bg)' : b.status === 'Rejected' ? 'var(--color-danger-bg)' : 'var(--color-info-bg)',
                        color: b.status === 'Approved' ? 'var(--color-success-text)' : b.status === 'Rejected' ? 'var(--color-danger-text)' : 'var(--color-info-text)'
                      }}>
                        {budgetStatusWord(t, b.status || 'Applied')}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {b.created_by && <div style={{ fontWeight: '600' }}>{b.created_by}</div>}
                      {b.created_at && <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{formatDateTime(b.created_at)}</div>}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {b.last_updated_by && <div style={{ fontWeight: '600' }}>{b.last_updated_by}</div>}
                      {b.last_updated_at && <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{formatDateTime(b.last_updated_at)}</div>}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      {hasAccessToRecord(b.deptCode, 'Budget', 'Edit') && (isGlobalAdmin || b.status !== 'Approved') && (
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button onClick={() => handleEditBudget(b)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-primary)', width: 'auto', display: 'inline-flex' }}>
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => handleDeleteBudget(b.id)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-danger)', width: 'auto', display: 'inline-flex' }}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'settlement' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.expId')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.invDate')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.deptTicSpaced')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.item')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.fundCode')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'right' }}>{t('budget.amt')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('common.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.method')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.transNo')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.invoice')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.receipt')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('common.remarks')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', fontSize: '0.875rem', textAlign: 'center' }}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenditures.length === 0 ? (
                  <tr><td colSpan="13" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('budget.noExpenditureRecords')}</td></tr>
                ) : (
                  paginatedExpenditures.map((f, i) => {
                    const isEditing = editingSettlementId === f.id;
                    return (
                      <tr key={f.id} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--color-bg-app)' }}>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: '500', whiteSpace: 'nowrap' }}>{f.expId}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{f.invDate}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: '600' }}>{f.deptCode}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{f.tic}</div>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{f.item}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{f.fundCode}</td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', textAlign: 'right', fontWeight: '600', whiteSpace: 'nowrap' }}>${Number(f.amt || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}</td>
                        
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                          {isEditing ? (
                            <select className="input-field" value={settlementFormData.settled} onChange={e => setSettlementFormData({...settlementFormData, settled: e.target.value})} style={{ minWidth: '100px', padding: '0.2rem' }}>
                              <option value="Pending">{t('budget.settledStatuses.pending')}</option>
                              <option value="Settled">{t('budget.settledStatuses.settled')}</option>
                              <option value="Rejected">{t('budget.settledStatuses.rejected')}</option>
                            </select>
                          ) : (
                            <span style={{ 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '1rem', 
                              fontSize: '0.75rem', 
                              fontWeight: '600', 
                              backgroundColor: f.settled === 'Settled' ? 'var(--color-success-light)' : (f.settled === 'Rejected' ? 'var(--color-danger-light)' : 'var(--color-warning-light)'),
                              color: f.settled === 'Settled' ? 'var(--color-success-dark)' : (f.settled === 'Rejected' ? 'var(--color-danger-dark)' : 'var(--color-warning-dark)')
                            }}>
                              {settledWord(t, f.settled || 'Pending')}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                          {isEditing ? (
                            <select className="input-field" value={settlementFormData.settlementMethod} onChange={e => setSettlementFormData({...settlementFormData, settlementMethod: e.target.value})} style={{ minWidth: '110px', padding: '0.2rem' }}>
                              <option value="">-</option>
                              <option value="Autopay">{t('budget.methods.autopay')}</option>
                              <option value="Cheque">{t('budget.methods.cheque')}</option>
                              <option value="Cash">{t('budget.methods.cash')}</option>
                              <option value="Bank Transfer">{t('budget.methods.banktransfer')}</option>
                              <option value="Others">{t('budget.methods.others')}</option>
                            </select>
                          ) : (
                            f.settlementMethod ? methodWord(t, f.settlementMethod) : '-'
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                          {isEditing ? (
                            <input type="text" className="input-field" value={settlementFormData.transNo} onChange={e => setSettlementFormData({...settlementFormData, transNo: e.target.value})} placeholder={t('budget.transNo')} style={{ minWidth: '100px', padding: '0.2rem' }} />
                          ) : (
                            f.transNo || '-'
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                          {isEditing ? (
                            <input type="text" className="input-field" value={settlementFormData.invoiceDoc} onChange={e => setSettlementFormData({...settlementFormData, invoiceDoc: e.target.value})} placeholder={t('budget.urlPlaceholder')} style={{ minWidth: '100px', padding: '0.2rem' }} />
                          ) : (
                            f.invoiceDoc ? <a href={f.invoiceDoc} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{t('budget.link')}</a> : '-'
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                          {isEditing ? (
                            <input type="text" className="input-field" value={settlementFormData.receiptDoc} onChange={e => setSettlementFormData({...settlementFormData, receiptDoc: e.target.value})} placeholder={t('budget.urlPlaceholder')} style={{ minWidth: '100px', padding: '0.2rem' }} />
                          ) : (
                            f.receiptDoc ? <a href={f.receiptDoc} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{t('budget.link')}</a> : '-'
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem' }}>
                          {isEditing ? (
                            <input type="text" className="input-field" value={settlementFormData.remarks} onChange={e => setSettlementFormData({...settlementFormData, remarks: e.target.value})} placeholder={t('common.remarks')} style={{ minWidth: '120px', padding: '0.2rem' }} />
                          ) : (
                            f.remarks || '-'
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', fontSize: '0.875rem', textAlign: 'center' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                              <button onClick={() => handleSaveSettlement(f.id)} className="btn-primary" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', minHeight: 'auto', height: 'auto', width: 'auto' }}>{t('common.save')}</button>
                              <button onClick={handleCancelSettlement} className="btn-outline" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', minHeight: 'auto', height: 'auto', width: 'auto' }}>{t('common.cancel')}</button>
                            </div>
                          ) : (
                            <button onClick={() => handleEditSettlement(f)} className="btn-outline" style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', minHeight: 'auto', height: 'auto', width: 'auto' }}>{t('common.edit')}</button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'settings' ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-md)', border: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>{t('budget.settingsTitle')}</h2>
                <button className="btn-primary" onClick={handleSaveSettings} disabled={savingSettings}>
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>

              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '1.5rem' }}>{t('budget.roleMatrix')}</h3>
              <div style={{ overflowX: 'auto' }}>
                <table className="theme-table">
                  <thead>
                    <tr>
                      <th style={{ width: '25%' }}>{t('budget.area')}</th>
                      <th style={{ width: '25%' }}>{t('dutyRoles.head')}</th>
                      <th style={{ width: '25%' }}>{t('dutyRoles.deputy')}</th>
                      <th style={{ width: '25%' }}>{t('dutyRoles.member')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'deptBudget', label: 'Department Budget' },
                      { key: 'commBudget', label: 'Committee Budget' },
                      { key: 'deptExpenditure', label: 'Department Expenditure' },
                      { key: 'commExpenditure', label: 'Committee Expenditure' },
                      { key: 'deptStatistics', label: 'Department Statistics' },
                      { key: 'commStatistics', label: 'Committee Statistics' }
                    ].map(area => (
                      <tr key={area.key}>
                        <td style={{ fontWeight: '500' }}>{area.label}</td>
                        {['Head', 'Deputy', 'Member'].map(role => (
                          <td key={role}>
                            <select 
                              className="input-field" 
                              value={settingsForm.roleMatrix[role][area.key]} 
                              onChange={(e) => {
                                const newMatrix = { ...settingsForm.roleMatrix };
                                newMatrix[role] = { ...newMatrix[role], [area.key]: e.target.value };
                                setSettingsForm({ ...settingsForm, roleMatrix: newMatrix });
                              }}
                            >
                              <option value="Edit">{t('budget.accessLevels.edit')}</option>
                              <option value="View Only">{t('budget.accessLevels.viewonly')}</option>
                              <option value="No Access">{t('budget.accessLevels.noaccess')}</option>
                            </select>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem', marginTop: '2rem' }}>{t('budget.globalRoles')}</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '2rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.accountants')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <select 
                      className="input-field" 
                      value={newAccountant}
                      onChange={(e) => setNewAccountant(e.target.value)}
                      style={{ flex: 3 }}
                    >
                      <option value="">{t('budget.selectStaff')}</option>
                      {staffs.filter(s => !settingsForm.accountants.includes(s)).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        if (newAccountant && !settingsForm.accountants.includes(newAccountant)) {
                          setSettingsForm(prev => ({ ...prev, accountants: [...prev.accountants, newAccountant].sort() }));
                          setNewAccountant('');
                        }
                      }}
                      disabled={!newAccountant}
                      style={{ flex: 1, padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}
                    >
                      Add
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {settingsForm.accountants.map(a => (
                      <span key={a} style={{ backgroundColor: 'var(--color-bg-app)', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px solid var(--color-border)' }}>
                        {a}
                        <button 
                          onClick={() => setSettingsForm(prev => ({ ...prev, accountants: prev.accountants.filter(x => x !== a) }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', display: 'flex', alignItems: 'center' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    ))}
                    {settingsForm.accountants.length === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.noAccountants')}</span>}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.budgetAdmins')}</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <select 
                      className="input-field" 
                      value={newBudgetAdmin}
                      onChange={(e) => setNewBudgetAdmin(e.target.value)}
                      style={{ flex: 3 }}
                    >
                      <option value="">{t('budget.selectStaff')}</option>
                      {staffs.filter(s => !settingsForm.budgetAdmins.includes(s)).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        if (newBudgetAdmin && !settingsForm.budgetAdmins.includes(newBudgetAdmin)) {
                          setSettingsForm(prev => ({ ...prev, budgetAdmins: [...prev.budgetAdmins, newBudgetAdmin].sort() }));
                          setNewBudgetAdmin('');
                        }
                      }}
                      disabled={!newBudgetAdmin}
                      style={{ flex: 1, padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}
                    >
                      Add
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {settingsForm.budgetAdmins.map(a => (
                      <span key={a} style={{ backgroundColor: 'var(--color-bg-app)', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px solid var(--color-border)' }}>
                        {a}
                        <button 
                          onClick={() => setSettingsForm(prev => ({ ...prev, budgetAdmins: prev.budgetAdmins.filter(x => x !== a) }))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', display: 'flex', alignItems: 'center' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    ))}
                    {settingsForm.budgetAdmins.length === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>{t('budget.noBudgetAdmins')}</span>}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-md)', border: '1px solid var(--color-border)' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>{t('budget.logTitle')}</h2>
            <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Export a detailed CSV report of all budget and expenditure modifications.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.startDate')}</label>
                <input type="date" className="input-field" value={logStartDate} onChange={e => setLogStartDate(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.endDate')}</label>
                <input type="date" className="input-field" value={logEndDate} onChange={e => setLogEndDate(e.target.value)} />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('budget.logType')}</label>
                <select className="input-field" value={logTypeFilter} onChange={e => setLogTypeFilter(e.target.value)}>
                  <option value="both">{t('budget.logBoth')}</option>
                  <option value="budget">{t('budget.tabs.budget')}</option>
                  <option value="expenditure">{t('budget.tabs.expenditure')}</option>
                </select>
              </div>
              <div style={{ flex: '0 0 auto' }}>
                <button onClick={handleExportLogCSV} disabled={logsLoading} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', cursor: logsLoading ? 'not-allowed' : 'pointer', opacity: logsLoading ? 0.7 : 1 }}>
                  <Download size={16} /> {logsLoading ? t('budget.exporting') : t('budget.exportLogCsv')}
                </button>
              </div>
            </div>
          </div>
          </div>
        ) : (
          <div style={{ padding: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'capitalize' }}>
              {activeTab}
            </h2>
            <p style={{ color: 'var(--color-text-muted)' }}>Content for {activeTab} will go here.</p>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default BudgetControl;
